'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Info } from 'lucide-react';
import { InfoModal } from '@/components/ui/InfoModal/InfoModal';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { TONE_SHARING_POLICY_URL, dismissToneSharingPolicyModal } from '@/utils/toneSharingPolicy';

export const ToneSharingPolicyLink = () => (
  <Link
    href={TONE_SHARING_POLICY_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-400 hover:underline"
  >
    Tone Sharing Guidelines
  </Link>
);

export const ToneSharingPolicyChanges = () => (
  <>
    <span>We&apos;ve updated the Tone Sharing Guidelines:</span>
    <ul className="list-disc pl-6 flex flex-col gap-1">
      <li>Captures of plugins, amp sims, and digital multi-FX effects are not allowed.</li>
      <li>
        Photos must be ones you personally took of the actual gear or signal chain you captured.
      </li>
      <li>
        Each tone pack must contain captures of a single piece of gear or signal chain. Don&apos;t
        mix different gear or organize packs around genres or artists.
      </li>
    </ul>
  </>
);

interface Props {
  open: boolean;
  onClose: () => void;
}

const ToneSharingPolicyModal = ({ open, onClose }: Props) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const close = () => {
    if (dontShowAgain) dismissToneSharingPolicyModal();
    onClose();
  };

  return (
    <InfoModal
      open={open}
      onClose={close}
      icon={<Info size={24} className="text-white" />}
      title="New Tone Sharing Guidelines"
      className="h-[520px] max-h-[none]"
      message={
        <div className="flex flex-col gap-4 text-zinc-400">
          <ToneSharingPolicyChanges />
          <span>
            These are just some of the changes. Please read the full <ToneSharingPolicyLink /> for
            complete details.
          </span>
          <div className="pt-2">
            <Checkbox
              label="Don't show me this again"
              checked={dontShowAgain}
              onChange={setDontShowAgain}
            />
          </div>
        </div>
      }
    />
  );
};

export default ToneSharingPolicyModal;
